interface ClaimSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  payoutAmount: number;
  onShare: () => void;
  isSharing?: boolean;
}

/**
 * ClaimSuccessModal
 * Displays a congratulatory message after a user successfully claims their rewards.
 * Encourages sharing with friends using the same share functionality.
 */
export function ClaimSuccessModal({
  isOpen,
  onClose,
  payoutAmount,
  onShare,
  isSharing = false,
}: ClaimSuccessModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="relative flex w-full max-w-sm flex-col bg-[#0c1c0c] border border-primary/20 rounded-3xl p-6 shadow-xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <span className="material-symbols-outlined text-white/60">
            close
          </span>
        </button>

        {/* Celebration Icon */}
        <div className="flex justify-center mb-4">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-primary/20">
            <span className="material-symbols-outlined text-primary text-5xl">
              celebration
            </span>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-white text-2xl font-bold text-center mb-2">
          Congratulations!
        </h2>

        {/* Payout Amount */}
        <div className="text-center mb-2">
          <span className="text-primary text-4xl font-extrabold">
            ${payoutAmount.toFixed(2)}
          </span>
        </div>

        {/* Description */}
        <p className="text-white/60 text-sm text-center mb-6">
          Your rewards have been claimed successfully! Share your achievement with friends.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {/* Share Button - Primary CTA */}
          <button
            onClick={onShare}
            disabled={isSharing}
            className={`w-full h-14 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-all ${
              isSharing
                ? 'bg-primary/70 text-[#0c1c0c]/70 cursor-not-allowed'
                : 'bg-primary text-[#0c1c0c] shadow-[0_0_20px_-5px_rgba(37,244,120,0.5)] active:scale-[0.98]'
            }`}
          >
            {isSharing ? (
              <>
                <span className="material-symbols-outlined animate-spin">sync</span>
                Generating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">ios_share</span>
                Share Your Win
              </>
            )}
          </button>

          {/* Done Button - Secondary */}
          <button
            onClick={onClose}
            className="w-full h-12 rounded-full border border-white/20 text-white/80 font-semibold flex items-center justify-center hover:bg-white/5 transition-colors active:scale-[0.98]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
